import { isContent } from './Content.js';
import { DataCellContent } from './DataColumn.js';
import messages from './intl/index.js';
import { getRowHandlers, getIndentPadding } from './Row.js';
import styles from './Table.module.scss.js';
import { nextSortDir, sortIcons } from './util.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useMessageFormatter } from '../i18n/format.js';
import { Button } from '../button/Button.js';
import { CheckboxInput } from '../inputs/CheckRadioInputs/CheckboxInput.js';
import { UNSAFE_Icon } from '../icon/Icon.js';
import { Text } from '../text/Text.js';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from '../node_modules/react-icons/md/index.js';
import classNames from 'clsx';
import { isAffirmativeKeypress } from '../util/aria.js';

function PrimaryHeaderCellContent(ctx) {
  const meta = ctx.column.columnDef.meta;
  const {
    formatMessage
  } = useMessageFormatter(messages);
  if (ctx.header.isPlaceholder) {
    return null;
  }
  const {
    anyExpandable,
    anySelectable,
    sorting
  } = ctx.table.options.meta;
  const thisSort = sorting?.[meta.key];
  const sortIconEl = thisSort && /*#__PURE__*/jsx(UNSAFE_Icon, {
    icon: sortIcons[thisSort],
    className: styles['icon-only']
  });
  const className = classNames(styles['header-cell-content'], meta.align && styles[`header-${meta.align}`]);
  const headerEl = meta.iconOnly ? /*#__PURE__*/jsx(UNSAFE_Icon, {
    icon: meta.icon,
    title: meta.heading,
    className: styles['icon-only']
  }) : /*#__PURE__*/jsx(Text, {
    icon: meta.icon,
    variant: "button-default",
    children: meta.heading
  });
  const spacerEl = anyExpandable ? /*#__PURE__*/jsx("span", {
    style: {
      width: 'calc(1.5rem - 1px)'
    }
  }) : null;
  const handleSelectAll = ctx.table.getToggleAllRowsSelectedHandler();
  const allSelected = ctx.table.getIsAllRowsSelected();
  const someSelected = ctx.table.getIsSomeRowsSelected();
  const checkEl = anySelectable ? /*#__PURE__*/jsx(CheckboxInput, {
    checked: allSelected,
    indeterminate: someSelected,
    onChange: handleSelectAll,
    "aria-label": formatMessage('Select all')
  }) : null;
  const sortHandlers = sortIconEl ? {
    role: 'button',
    tabIndex: 0,
    onClick: () => meta.onSort?.(nextSortDir(thisSort)),
    onKeyDown: isAffirmativeKeypress(() => meta.onSort?.(nextSortDir(thisSort)))
  } : {};
  return /*#__PURE__*/jsxs("div", {
    className: className,
    ...sortHandlers,
    children: [spacerEl, checkEl, /*#__PURE__*/jsxs("div", {
      className: styles['header-label-and-sort'],
      children: [headerEl, sortIconEl]
    })]
  });
}
function PrimaryCellContent(ctx) {
  const {
    row,
    table
  } = ctx;
  const o = row.original;
  const {
    formatMessage
  } = useMessageFormatter(messages);
  if (isContent(o)) {
    return;
  }
  const tableMeta = table.options.meta;
  const {
    handleExpand,
    handleSelect,
    expandDisabled,
    primaryRowAction
  } = getRowHandlers(row, tableMeta);
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  let expanderEl = null;
  const hasPrimaryRowAction = !!primaryRowAction;

  // if theres a primary row action skip the button
  if (tableMeta.anyExpandable) {
    expanderEl = /*#__PURE__*/jsx(Button, {
      tabIndex: hasPrimaryRowAction ? -1 : 0,
      className: classNames(!canExpand && styles['hidden']),
      variant: "ui-control",
      size: "small-compact",
      onClick: () => handleExpand?.(),
      disabled: expandDisabled,
      iconOnly: true,
      icon: isExpanded ? MdKeyboardArrowDown : MdKeyboardArrowRight,
      "data-table-internal-input": true,
      children: formatMessage('Expand')
    });
  }
  const checkboxEl = tableMeta.selectionMode === 'multi' ? /*#__PURE__*/jsx(CheckboxInput, {
    tabIndex: tableMeta.canFocusRows ? -1 : 0,
    className: styles['row-checkbox'],
    disabled: !row.getCanSelect(),
    checked: row.getIsSelected()
    // https://github.com/TanStack/table/issues/5620
    // indeterminate={row.getIsSomeSelected()}
    ,
    indeterminate: isIndeterminate(row),
    onChange: handleSelect,
    "data-table-internal-input": true,
    "aria-label": formatMessage('Select')
  }) : null;
  const paddingLeft = getIndentPadding(row);
  const cellStyle = {
    paddingLeft
  };
  return /*#__PURE__*/jsxs("div", {
    className: styles['primary-cell-content'],
    style: cellStyle,
    children: [expanderEl, checkboxEl, /*#__PURE__*/jsx(DataCellContent, {
      ...ctx
    })]
  });
}
function isIndeterminate(row) {
  const dataRows = row.subRows.filter(x => !isContent(x.original));
  const noneSelected = dataRows.every(x => !x.getIsSelected());
  const allSelected = dataRows.every(x => x.getIsSelected());
  return !noneSelected && !allSelected;
}

export { PrimaryCellContent, PrimaryHeaderCellContent };
